// src/pages/profesor/ProfesorLayout.tsx
import { useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  Box,
} from "@mui/material";

import { NavLink, Outlet, useMatch, useResolvedPath } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NotificationBell from "../../components/NotificationBell";
import AccountMenu from "../../components/AccountMenu";
import LanguageSwitch from "../../components/LanguageSwitch";
import HeaderFirmasBadge from "../../components/HeaderFirmasBadge";
import DashboardIcon from "../../imagenes/Iconos/Dashboard.svg";
import AgendaIcon from "../../imagenes/Iconos/Agenda.svg";
import VacacionesIcon from "../../imagenes/Iconos/Vacaciones.svg";
import DocumentosIcon from "../../imagenes/Iconos/Documentos.svg";
import CuentaIcon from "../../imagenes/Iconos/Cuenta.svg";
import GorillaHomeIcon from "../../imagenes/Iconos/GorillaHome.svg";
import HrInquiryButton from "../../components/TicketButtons/HrInquiryButton";

const drawerWidth = 220;

/* helper item -------------------------------------------------- */
function Item({
  to,
  icon,
  label,
  end = false, // ← exact match opcional (true para Inicio)
}: {
  to: string;
  icon: JSX.Element;
  label: string;
  end?: boolean;
}) {
  // Calculamos si está activo con React Router y usamos 'selected' de MUI
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end });

  return (
    <ListItemButton
      component={NavLink}
      to={to}
      // En lugar de depender de '.active', usamos el estado 'selected'
      selected={!!match}
      sx={{
        color: "#f9f9f9",
        fontWeight: 600,
        minHeight: 40, // ⬅️ más bajo que el default (48px)
        py: 0.5,
        "&.Mui-selected": {
          bgcolor: "#21a598",
          color: "#ffffff",
          "& .MuiListItemIcon-root": { color: "#ffffff" },
          "& .MuiListItemText-root": { color: "#ffffff", fontWeight: 600 },
        },
        "&.Mui-selected:hover": { bgcolor: "#1b877dff" },
        "&:hover": { bgcolor: "#121212ff" },
      }}
    >
      <ListItemIcon sx={{ color: "inherit" }}>{icon}</ListItemIcon>
      <ListItemText primary={label} sx={{ color: "inherit", fontWeight: 600 }} />
    </ListItemButton>
  );
}

export default function ProfesorLayout() {
  const { t } = useTranslation<"translation">();
  const [open, setOpen] = useState(true);

  // Fallback i18n para evitar mostrar la clave literal si no existe
  const hrKey = "profesorLayout.hrButton";
  const hrText = t(hrKey);
  const hrLabel = hrText === hrKey ? "Contactar con HR" : hrText;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f2f3f4" }}>
      {/* ===== App-Bar superior ===== */}
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "#000 !important",
          color: "#f9f9f4 !important",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {/* Usamos un layout de tres zonas: izquierda (logos), centro (botón HR), derecha (iconos/menus) */}
        <Toolbar variant="dense" sx={{ px: 2, display: "flex", alignItems: "center", gap: 2 }}>
          {/* Zona izquierda ---------------------------------------------------- */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <img
              src={GorillaHomeIcon}
              alt="Gorilla Home"
              style={{
                height: 48,
                objectFit: "contain",
                marginRight: 0,
                cursor: "pointer",
                paddingTop: 2, // 2 píxeles de padding arriba
              }}
              onClick={() => setOpen(!open)}
            />
            <img
              src={require("../../imagenes/GorillaLogoHeader.png")}
              alt="Gorilla Logo"
              style={{
                height: 40, // ajusta el tamaño según tu header
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Zona centro: botón HR centrado ----------------------------------- */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <HrInquiryButton
              buttonText={hrLabel}
              variant="contained"
              color="warning"
              size="small"
              appearance="firmas" // ⬅️ estilo idéntico al botón de Firmas
              onSuccess={() => {}}
            />
          </Box>

          {/* Zona derecha ------------------------------------------------------ */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ mr: 1 }}>
              <HeaderFirmasBadge />
            </Box>
            <Box
              sx={{
                "& svg": {
                  color: "#f9f9f4 !important",
                  fill: "#f9f9f4 !important",
                },
              }}
            >
              <NotificationBell />
            </Box>
            <Box
              sx={{
                "& svg": {
                  color: "#f9f9f4 !important",
                  fill: "#f9f9f4 !important",
                },
              }}
            >
              <LanguageSwitch />
            </Box>
            <Box sx={{ color: "#f9f9f4" }}>
              <AccountMenu />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ===== Drawer lateral ===== */}
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          width: open ? drawerWidth : 56,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : 56,
            transition: (theme) => theme.transitions.create("width"),
            boxSizing: "border-box",
            backgroundColor: "#1c1e1dff",
            color: "#f9f9f4",
          },
        }}
      >
        <Toolbar variant="dense" />

        <List disablePadding>
          {/* Inicio / Dashboard — EXACTO */}
          <Item
            to="/profesor"
            end
            icon={<img src={DashboardIcon} alt="Dashboard" style={{ width: 42, height: 42 }} />}
            label={t("profesorLayout.menu.dashboard")}
          />
          <Item
            to="/profesor/agenda"
            icon={<img src={AgendaIcon} alt="Agenda" style={{ width: 42, height: 42 }} />}
            label={t("profesorLayout.menu.agenda")}
          />
          <Item
            to="/profesor/vacaciones"
            icon={<img src={VacacionesIcon} alt="Vacaciones" style={{ width: 42, height: 42 }} />}
            label={t("profesorLayout.menu.vacaciones")}
          />
          <Item
            to="/profesor/documentos"
            icon={<img src={DocumentosIcon} alt="Documentos" style={{ width: 42, height: 42 }} />}
            label={t("profesorLayout.menu.documentos")}
          />
          <Item
            to="/profesor/cuenta"
            icon={<img src={CuentaIcon} alt="Cuenta" style={{ width: 42, height: 42 }} />}
            label={t("profesorLayout.menu.cuenta")}
          />
        </List>
      </Drawer>

      {/* ===== Contenido principal ===== */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar variant="dense" />
        <Outlet />
      </Box>
    </Box>
  );
}
